import Axios from 'api/client';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';
import {
    Workflow,
    WorkflowActionMetrics,
    WorkflowLog,
    WorkflowLogStatus,
    WorkflowMetrics,
    WorkflowStatus,
    WorkflowStep,
    WorkflowUsage,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface Workflows extends PaginatedData<Workflow> {
    totals: {
        total: number;
        total_draft: number;
        total_paused: number;
        total_active: number;
    } | null;
}

interface FetchWorkflowsParams extends FilterParams {
    statuses?: WorkflowStatus[];
}

interface FetchWorkflowStepsParams {
    version?: number;
}

interface CreateWorkflowParams {
    trigger: WorkflowTriggerTypeEnum;
    name: string;
    icon: string;
    execute_once?: boolean;
}

interface UpdateWorkflowParams {
    name: string;
    icon: string;
    trigger: WorkflowTriggerTypeEnum;
    execute_once: boolean;
}

interface UpdateWorkflowStatusParams {
    status: WorkflowStatus;
}

interface UpdateWorkflowStepsParams {
    steps: Omit<WorkflowStep, 'id' | 'order'>[];
    workflow_updated_at: string | null;
}

interface FetchWorkflowLogsParams extends FilterParams {
    statuses?: WorkflowLogStatus[];
}

type WorkflowStepApiResponse = WorkflowStep & {
    created_at: string | null;
    updated_at: string | null;
};

interface UpdateWorkflowStepsResponse {
    data: {
        steps: WorkflowStepApiResponse[];
        workflow: Workflow;
    };
}

const fetchWorkflows = (accountId: number, params: FetchWorkflowsParams) =>
    Axios.get<Workflows>(`/accounts/${accountId}/workflows`, { params }).then(
        response => response.data,
    );

const fetchWorkflowById = (accountId: number, workflowId: number) =>
    Axios.get<Workflow>(`/accounts/${accountId}/workflows/${workflowId}`).then(
        response => response.data,
    );

const createWorkflow = (accountId: number, params: CreateWorkflowParams) =>
    Axios.post<Workflow>(`/accounts/${accountId}/workflows`, params).then(
        response => response.data,
    );

const updateWorkflow = (
    accountId: number,
    workflowId: number,
    params: Partial<UpdateWorkflowParams>,
) =>
    Axios.patch<Workflow>(`/accounts/${accountId}/workflows/${workflowId}`, params).then(
        response => response.data,
    );

const deleteWorkflow = (accountId: number, workflowId: number) =>
    Axios.delete(`/accounts/${accountId}/workflows/${workflowId}`).then(response => response.data);

const duplicateWorkflow = (accountId: number, workflowId: number) =>
    Axios.post<Workflow>(`/accounts/${accountId}/workflows/${workflowId}/duplicate`).then(
        response => response.data,
    );

const publishWorkflowVersion = (accountId: number, workflowId: number) =>
    Axios.patch(`/accounts/${accountId}/workflows/${workflowId}/versions`).then(
        response => response.data,
    );

const deleteDraftWorkflowVersion = (accountId: number, workflowId: number) =>
    Axios.delete(`/accounts/${accountId}/workflows/${workflowId}/versions/draft`).then(
        response => response.data,
    );

const updateWorkflowStatus = (
    accountId: number,
    workflowId: number,
    params: UpdateWorkflowStatusParams,
) =>
    Axios.patch<Workflow>(`/accounts/${accountId}/workflows/${workflowId}/statuses`, params).then(
        response => response.data,
    );

const fetchWorkflowStepsById = (
    accountId: number,
    workflowId: number,
    params: FetchWorkflowStepsParams,
) =>
    Axios.get<WorkflowStepApiResponse[]>(`/accounts/${accountId}/workflows/${workflowId}/steps`, {
        params,
    }).then(response => response.data);

const updateWorkflowSteps = (
    accountId: number,
    workflowId: number,
    params: UpdateWorkflowStepsParams,
) =>
    Axios.patch<UpdateWorkflowStepsResponse>(
        `/accounts/${accountId}/workflows/${workflowId}/steps`,
        params,
    ).then(response => response.data);

const fetchWorkflowLogs = (
    accountId: number,
    workflowId: number,
    params: FetchWorkflowLogsParams,
) =>
    Axios.get<PaginatedData<WorkflowLog>>(`/accounts/${accountId}/workflows/${workflowId}/logs`, {
        params,
    }).then(response => response.data);

const fetchWorkflowUsage = (accountId: number, workflowId: number) =>
    Axios.get<WorkflowUsage>(`/accounts/${accountId}/workflows/${workflowId}/usage`).then(
        response => response.data,
    );

const fetchWorkflowActionMetrics = (accountId: number, workflowId: number) =>
    Axios.get<WorkflowActionMetrics[]>(
        `/accounts/${accountId}/workflows/${workflowId}/action-metrics`,
    ).then(response => response.data);

const fetchWorkflowMetrics = (accountId: number, workflowId: number) =>
    Axios.get<WorkflowMetrics>(`/accounts/${accountId}/workflows/${workflowId}/metrics`).then(
        response => response.data,
    );

export {
    // workflows
    fetchWorkflows,
    fetchWorkflowById,
    createWorkflow,
    updateWorkflow,
    deleteWorkflow,
    duplicateWorkflow,
    updateWorkflowStatus,
    publishWorkflowVersion,
    deleteDraftWorkflowVersion,

    // workflow steps
    fetchWorkflowStepsById,
    updateWorkflowSteps,

    // workflowLogs
    fetchWorkflowLogs,

    // statistics
    fetchWorkflowUsage,
    fetchWorkflowActionMetrics,
    fetchWorkflowMetrics,
};
export type {
    FetchWorkflowsParams,
    FetchWorkflowLogsParams,
    CreateWorkflowParams,
    UpdateWorkflowParams,
    UpdateWorkflowStepsParams,
    UpdateWorkflowStatusParams,
    WorkflowStatus,
    FetchWorkflowStepsParams,
    WorkflowStepApiResponse,
    UpdateWorkflowStepsResponse,
};
